/**
 * Captura parâmetros de rastreamento pra anexar ao lead que vai pro HubSpot.
 * Mesmo padrão usado no projeto consorcio-allu (UTM + Meta + Google).
 *
 * Os nomes das propriedades batem com as custom properties já existentes
 * no HubSpot da Allu (criadas pelo time do consorcio).
 */

export interface TrackingParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ac_fbclid?: string;
  fbc?: string;
  fbp?: string;
  ac_gclid?: string;
  hubspotutk?: string;
  ac_lead_source_lp?: string;
  page_uri?: string;
  referrer?: string;
}

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[2]) : undefined;
}

function getQueryParam(name: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const v = new URL(window.location.href).searchParams.get(name);
  return v ?? undefined;
}

/**
 * Persistência de primeiro toque (sessionStorage, morre com a aba).
 *
 * Por quê: funis multi-página (ex.: /evento → /evento/aplicar) navegam via
 * <Link> sem repassar a query string. Sem isso, o lead que clicou no anúncio
 * mas submete o form em OUTRA página sobe pro HubSpot sem UTM — foi exatamente
 * o buraco de atribuição encontrado nos leads do funil /evento.
 */
const STORAGE_KEY = "allu_tracking_params";

type PersistedKey =
  | "utm_source"
  | "utm_medium"
  | "utm_campaign"
  | "utm_content"
  | "utm_term"
  | "ac_fbclid"
  | "ac_gclid";

const URL_PARAM_TO_KEY: Record<string, PersistedKey> = {
  utm_source: "utm_source",
  utm_medium: "utm_medium",
  utm_campaign: "utm_campaign",
  utm_content: "utm_content",
  utm_term: "utm_term",
  fbclid: "ac_fbclid",
  gclid: "ac_gclid",
};

function readStoredParams(): Partial<Record<PersistedKey, string>> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Record<PersistedKey, string>>) : {};
  } catch {
    return {};
  }
}

/**
 * Captura UTM/fbclid/gclid da URL atual e guarda na sessão. Chamar no load do
 * app (main.tsx) — o React Router não recarrega a página, então o load sempre
 * acontece na URL de aterrissagem, que é onde as UTMs estão.
 * Toque novo com UTM sobrescreve o anterior (last-touch da sessão).
 */
export function persistTrackingParams(): void {
  if (typeof window === "undefined") return;
  const fresh: Partial<Record<PersistedKey, string>> = {};
  for (const [param, key] of Object.entries(URL_PARAM_TO_KEY)) {
    const v = getQueryParam(param);
    if (v) fresh[key] = v;
  }
  if (Object.keys(fresh).length === 0) return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readStoredParams(), ...fresh })
    );
  } catch {
    /* sessionStorage indisponível (Safari private antigo etc.) — segue sem persistir */
  }
}

/**
 * Lê UTM + fbclid + gclid da URL + cookies de tracking (_fbc/_fbp/hubspotutk).
 * Pode ser chamado a qualquer momento — captura o estado atual da página.
 */
export function getTrackingParams(): TrackingParams {
  const params: TrackingParams = {
    ac_lead_source_lp: "vendedores_externos",
  };

  // Se a própria página do form for a aterrissagem (link direto com UTM),
  // garante a captura mesmo sem ter passado pelo load do app.
  persistTrackingParams();
  const stored = readStoredParams();

  // UTM params — URL atual primeiro; se não tiver (navegou via <Link> pra
  // outra página do funil), cai pro que foi persistido na aterrissagem.
  const utm_source = getQueryParam("utm_source") ?? stored.utm_source;
  const utm_medium = getQueryParam("utm_medium") ?? stored.utm_medium;
  const utm_campaign = getQueryParam("utm_campaign") ?? stored.utm_campaign;
  const utm_content = getQueryParam("utm_content") ?? stored.utm_content;
  const utm_term = getQueryParam("utm_term") ?? stored.utm_term;
  if (utm_source) params.utm_source = utm_source;
  if (utm_medium) params.utm_medium = utm_medium;
  if (utm_campaign) params.utm_campaign = utm_campaign;
  if (utm_content) params.utm_content = utm_content;
  if (utm_term) params.utm_term = utm_term;

  // Meta Ads tracking
  const fbclid = getQueryParam("fbclid") ?? stored.ac_fbclid;
  if (fbclid) params.ac_fbclid = fbclid;
  const fbc = getCookie("_fbc");
  if (fbc) params.fbc = fbc;
  const fbp = getCookie("_fbp");
  if (fbp) params.fbp = fbp;

  // Google Ads tracking
  const gclid = getQueryParam("gclid") ?? stored.ac_gclid;
  if (gclid) params.ac_gclid = gclid;

  // HubSpot tracking cookie (preserva attribution histórica do contato)
  const hubspotutk = getCookie("hubspotutk");
  if (hubspotutk) params.hubspotutk = hubspotutk;

  // Page context
  if (typeof window !== "undefined") {
    params.page_uri = window.location.href;
  }
  if (typeof document !== "undefined" && document.referrer) {
    params.referrer = document.referrer;
  }

  return params;
}
